/*
  Sidebar — Cliniko-style left navigation for desktop (md+ screens).

  - Toggle-collapsible (persisted in localStorage as `sidebar-collapsed`).
  - Sections: Primary / Practice / Manage — filtered by user role by
    the caller before being passed as props.
  - Active state: subtle left accent stripe + light indigo bg tint.
  - When collapsed, labels hide and the icon shows a tooltip on hover
    (native `title` — good enough, avoids extra deps).

  Kept intentionally free of API calls or auth logic — the caller is
  responsible for role filtering.
*/
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@/lib/utils";

const COLLAPSE_KEY = "sidebar-collapsed";

// A nav item can also own other routes (e.g. Reports owns every report page).
export const navItemActive = (it, isActive, pathname) =>
  isActive || !!it.match?.some((p) => pathname === p || pathname.startsWith(`${p}/`));

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
}
function writeCollapsed(v) {
  try { localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0"); } catch { /* ignore */ }
}

export default function Sidebar({ sections }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => { writeCollapsed(collapsed); }, [collapsed]);

  return (
    <aside
      className={cn(
        "hidden md:flex sticky top-16 self-start h-[calc(100vh-4rem)]",
        "flex-col border-r border-zinc-200 bg-le-sidebar transition-[width] duration-200 ease-out",
        collapsed ? "w-[68px]" : "w-[240px]"
      )}
      data-testid="app-sidebar"
      data-collapsed={collapsed ? "1" : "0"}
    >
      <div className="flex items-center justify-end px-3 py-2 border-b border-zinc-200/70">
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="h-7 w-7 grid place-items-center rounded-md text-zinc-500 hover:bg-le-selected/60 hover:text-zinc-950 transition-colors"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-2 space-y-1 thin-scroll">
        {sections.map((section) => (
          section.items.length > 0 && (
            <SidebarSection
              key={section.key}
              label={section.label}
              items={section.items}
              collapsed={collapsed}
            />
          )
        ))}
      </nav>
    </aside>
  );
}

function SidebarSection({ label, items, collapsed }) {
  const { pathname } = useLocation();
  return (
    <div className="px-2 pb-2">
      {!collapsed && (
        <div className="px-2.5 pt-3 pb-1 text-xs text-zinc-500 font-semibold select-none">
          {label}
        </div>
      )}
      {collapsed && <div className="mt-2 mx-3 border-t border-zinc-200" />}
      <ul className="space-y-0.5 mt-1">
        {items.map((it) => (
          <li key={it.to}>
            <NavLink
              to={it.to}
              end={it.to === "/"}
              data-testid={`sb-${it.testId}`}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                cn(
                  "group relative flex items-center gap-2.5 rounded-md text-sm transition-colors",
                  collapsed ? "px-2.5 py-2 justify-center" : "px-2.5 py-2",
                  navItemActive(it, isActive, pathname)
                    ? "bg-le-selected text-le-primary-deep font-semibold"
                    : "text-zinc-700 hover:bg-le-selected/50 hover:text-zinc-950"
                )
              }
            >
              {({ isActive: linkActive }) => {
                const isActive = navItemActive(it, linkActive, pathname);
                return (
                <>
                  {isActive && (
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-le-primary" aria-hidden />
                  )}
                  <it.icon
                    className={cn("h-[18px] w-[18px] flex-shrink-0", isActive ? "text-le-primary-text" : "text-zinc-500 group-hover:text-zinc-800")}
                    strokeWidth={2}
                  />
                  {!collapsed && <span className="truncate">{it.label}</span>}
                </>
                );
              }}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
